import React, { useState } from 'react';
import Select, { components } from 'react-select';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';

const Option = props => (
	<components.Option {...props}>
		<input
			type="checkbox"
			checked={props.isSelected}
			readOnly
			tabIndex={-1}
			aria-hidden="true"
		/>
		<span className="column-selector__option-label">{props.label}</span>
	</components.Option>
);

const selectStyles = {
	option: (provided, state) => ({
		...provided,
		padding: 12,
		textAlign: 'left',
		color: '#101323',
		fontFamily: 'Manrope',
		fontSize: 14,
		fontWeight: 400,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		width: '100%',
		cursor: 'pointer',
		boxSizing: 'border-box',
		backgroundColor: state.isFocused ? '#F4F3FF' : '#FFFFFF',
		':active': {
			backgroundColor: state.isFocused ? '#F4F3FF' : '#FFFFFF'
		}
	}),
	menu: provided => ({
		...provided
	})
};

export default function ColumnSelector({ target, options, value, onChange }) {
	const [isOpen, setIsOpen] = useState(false);

	// Floating UI setup
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(8), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
		placement: 'bottom-end'
	});

	// Interaction hooks
	const click = useClick(context, {
		preventScroll: true
	});
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	return (
		<>
			{target({
				ref: refs.setReference,
				...getReferenceProps()
			})}

			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager context={context} modal={false}>
						<div
							ref={refs.setFloating}
							style={{ ...floatingStyles, zIndex: 1000 }}
							{...getFloatingProps()}
							className="column-selector-container">
							<Select
								classNamePrefix="column-selector"
								tabSelectsValue={false}
								controlShouldRenderValue={false}
								closeMenuOnSelect={false}
								isClearable={false}
								isMulti
								menuIsOpen
								components={{
									Option,
									IndicatorSeparator: null,
									DropdownIndicator: null
								}}
								options={options}
								hideSelectedOptions={false}
								backspaceRemovesValue={false}
								onChange={e => onChange(e)}
								value={value}
								styles={selectStyles}
								menuPlacement="auto"
							/>
						</div>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</>
	);
}
